import { useCallback, useMemo, useState } from 'react';

import { getTelehealthReport, type Grade } from '@/api/grade';

export type UseTelehealthReportDownloadOptions = {
  grades?: Grade[];
  patientName?: string | null;
  patientId?: string;
  caseId: string;
};

export function useTelehealthReportDownload(options: UseTelehealthReportDownloadOptions) {
  const { grades, patientName, patientId, caseId } = options;
  const [downloading, setDownloading] = useState(false);

  const gradeToDownload = useMemo(() => {
    if (!grades?.length) return undefined;

    return grades.reduce<Grade | undefined>((latest, grade) => {
      if (!latest) return grade;

      const latestTime = Date.parse(latest.createdUtc);
      const gradeTime = Date.parse(grade.createdUtc);

      if (Number.isNaN(gradeTime)) return latest;
      if (Number.isNaN(latestTime)) return grade;

      return gradeTime > latestTime ? grade : latest;
    }, undefined);
  }, [grades]);

  const reportFileName = useMemo(() => {
    const trimmedName = patientName?.trim();
    const identifier = trimmedName && trimmedName.length > 0 ? trimmedName : (patientId ?? caseId);
    return `Telehealth_Report - ${identifier}.pdf`;
  }, [patientName, patientId, caseId]);

  const downloadReport = useCallback(async () => {
    if (!gradeToDownload) return;

    setDownloading(true);
    try {
      const file = await getTelehealthReport(gradeToDownload.grade_id);
      if (!file) return;

      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = reportFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading telehealth report', error);
    } finally {
      setDownloading(false);
    }
  }, [gradeToDownload, reportFileName]);

  return {
    downloadReport,
    downloading,
    canDownload: Boolean(gradeToDownload),
    gradeToDownload,
    reportFileName,
  } as const;
}
